import React from 'react';
import { Box } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { formatTimeRemaining } from '../../utils/timerUtils';

interface TimerCountdownProps {
  timeRemaining: number;
  isActive: boolean;
  showIcon?: boolean;
  size?: 'small' | 'medium' | 'large';
  position?: 'left' | 'right'; // 'left' para posicionar desde la izquierda, 'right' desde la derecha
}

/**
 * Componente para mostrar el cronómetro de cuenta regresiva
 * Posicionado a la derecha del botón con estilo minimalista
 */
const TimerCountdown: React.FC<TimerCountdownProps> = ({ 
  timeRemaining, 
  isActive, 
  showIcon = true,
  size = 'small',
  position = 'right' // Por defecto desde la derecha (como DeviceCard)
}) => {
  console.log('🔍 TimerCountdown Debug:', { timeRemaining, isActive, showIcon, size });
  
  if (!isActive || timeRemaining <= 0) {
    console.log('🔍 TimerCountdown: No se renderiza - isActive:', isActive, 'timeRemaining:', timeRemaining);
    return null;
  }

  // Configuración de tamaños (30% más chico)
  const sizeConfig = {
  small: {
    fontSize: '0.56rem', // Un poco más grande que 0.49
    padding: '1px 8px',   // Mitad de la diferencia (era 2px, ahora 1px)
    iconSize: '0.48rem',  // Un poco más grande
    gap: '4px'            // Un poco más grande
  },
    medium: {
      fontSize: '0.56rem', // 0.8 * 0.7 = 0.56
      padding: '3px 7px',  // Reducido 30%
      iconSize: '0.49rem', // 0.7 * 0.7 = 0.49
      gap: '4px'           // Reducido 30%
    },
    large: {
      fontSize: '0.63rem', // 0.9 * 0.7 = 0.63
      padding: '4px 8px',  // Reducido 30%
      iconSize: '0.56rem', // 0.8 * 0.7 = 0.56
      gap: '6px'           // Reducido 30%
    }
  };

  const config = sizeConfig[size];

  return (
    <Box
      sx={{
        position: 'absolute',
        // Posicionamiento dinámico según prop 'position'
        // NOTA: Cuando position='right', el TimerCountdown se renderiza directamente en DeviceCard, no aquí
        ...(position === 'right' 
          ? { right: '35px' } // Solo para compatibilidad, pero no se usa cuando se renderiza desde DeviceCard
          : { left: '55px' }  // Desde la izquierda (DeviceModal para no-PLUS2PM)
        ),
        bottom: position === 'left' ? '33.8px' : '3.8px', // Solo para compatibilidad cuando position='right'
        display: 'flex',
        alignItems: 'center',
        justifyContent: position === 'right' ? 'flex-end' : 'flex-start',
        gap: config.gap,
        fontSize: config.fontSize,
        color: '#2391FF',
        fontWeight: '600',
        backgroundColor: 'rgba(35, 145, 255, 0.1)',
        border: '1px solid #2391FF',
        borderRadius: '8.2px', // Mismo borderRadius que offline, sin respuesta y auth
        padding: config.padding,
        lineHeight: 1.0, // Misma altura que "Sin respuesta"
        whiteSpace: 'nowrap',
        minWidth: '50px', // Ancho mínimo
        maxWidth: '150px', // Ancho máximo para timers muy largos
        textAlign: position === 'right' ? 'right' : 'left',
        transition: 'all 0.3s ease-in-out',
        zIndex: 10, // Asegurar que esté por encima
        // Animación sutil cuando está por terminar
        animation: timeRemaining <= 10 ? 'countdownWarning 1s ease-in-out infinite' : 'none',
        '@keyframes countdownWarning': {
          '0%': {
            backgroundColor: 'rgba(35, 145, 255, 0.1)',
            borderColor: '#2391FF',
          },
          '50%': {
            backgroundColor: 'rgba(255, 87, 87, 0.2)',
            borderColor: '#FF5757',
            color: '#FF5757',
          },
          '100%': {
            backgroundColor: 'rgba(35, 145, 255, 0.1)',
            borderColor: '#2391FF',
            color: '#2391FF',
          },
        },
        // Hover effect
        '&:hover': {
          backgroundColor: 'rgba(35, 145, 255, 0.15)',
          transform: 'scale(1.05)',
        },
      }}
    >
      {showIcon && (
        <AccessTimeIcon 
          sx={{ 
            fontSize: config.iconSize,
            color: 'inherit'
          }} 
        />
      )}
      <span>
        {formatTimeRemaining(timeRemaining)}
      </span>
    </Box>
  );
};

export default TimerCountdown;


